import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError, meterKeys } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { accountKeys } from "./use-accounts";

type Account = components["schemas"]["Account"];
type AccountUpdate = components["schemas"]["AccountUpdate"];
type AccountPremiseListResponse = components["schemas"]["AccountPremiseListResponse"];
type AccountDeviceInstallationListResponse =
  components["schemas"]["AccountDeviceInstallationListResponse"];
type AccountMeterListResponse = components["schemas"]["AccountMeterListResponse"];
type AccountBilledInstallationListResponse =
  components["schemas"]["BilledInstallationListResponse"];
type DeviceLifecycleStatus = components["schemas"]["DeviceLifecycleStatus"];

const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;
const DETAIL_STALE_TIME = 2 * 60_000;
const DETAIL_GC_TIME = 10 * 60_000;

export interface AccountPremiseListParams {
  name_search?: string;
  limit?: number;
  offset?: number;
  sort_by?: "facility_name" | "compliance_status";
  sort_dir?: "asc" | "desc";
}

export interface AccountDeviceInstallationListParams {
  serial_search?: string;
  facility_id?: string;
  device_status?: string;
  limit?: number;
  offset?: number;
  sort_by?: "inventory_serial" | "facility_name" | "device_status" | "created_at" | "verdict";
  sort_dir?: "asc" | "desc";
}

export interface AccountBilledInstallationListParams {
  search?: string;
  device_status?: DeviceLifecycleStatus;
  limit?: number;
  offset?: number;
  sort_by?: "serial_number" | "meter_number" | "facility_name" | "device_status";
  sort_dir?: "asc" | "desc";
}

/**
 * The reverse meter lookup takes no filter or sort — the endpoint offers only
 * `limit` / `offset` and returns rows already ordered by normalized meter
 * number.
 */
export interface AccountMeterListParams {
  limit?: number;
  offset?: number;
}

/**
 * Sub-key extensions for reverse-lookup caches. Composed on top of
 * `accountKeys.detail(id)` so invalidation of the detail flushes the
 * reverse-lookup sub-trees too.
 */
const premiseSubKey = (id: string, params: AccountPremiseListParams) =>
  [...accountKeys.detail(id), "premises", params] as const;

const installationSubKey = (id: string, params: AccountDeviceInstallationListParams) =>
  [...accountKeys.detail(id), "device-installations", params] as const;

export function useAccount(id: string | null | undefined) {
  return useQuery<Account>({
    queryKey: accountKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) throw new Error("Account id is required");
      const { data, error, response } = await apiClient.GET("/accounts/{id}", {
        params: { path: { id } },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load account";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

export function useUpdateAccount(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccountUpdate) => {
      const { data, error, response } = await apiClient.PATCH("/accounts/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update account";
        throw new ApiError(
          response.status,
          msg,
          (error as { code?: string }).code,
          (error as { details?: unknown }).details,
        );
      }
      return data as Account;
    },
    onSuccess: (data) => {
      // The PATCH response IS the canonical detail shape — write it into the
      // cache so the field shows server truth the moment the commit resolves,
      // with no invalidate-then-refetch gap. Lists still refetch (server-derived
      // columns). Safe here and NOWHERE else: GetAccount and UpdateAccount both
      // return the same full projection, unlike the facility/device write paths.
      queryClient.setQueryData(accountKeys.detail(id), data);
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

/**
 * useDeleteAccount soft-deletes an account row. The 409 envelope is unified
 * per spec §Error Envelopes — discriminated by `code` between the
 * dependent-installations and soft-delete-immutable branches. The typed
 * payload is threaded through `ApiError.details` (NOT collapsed to a
 * `.message` string) so the modal can enumerate the blocking rows or render
 * the `deleted_at` timestamp.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await apiClient.DELETE("/accounts/{id}", {
        params: { path: { id } },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to delete account";
        // The 409 envelope carries `code`, the dependent-row counts and ids,
        // and optionally `deleted_at`. The whole envelope is threaded through
        // `ApiError.details` so consumers can narrow on `code`.
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({
        queryKey: accountKeys.detail(id),
      });
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useAccountPremises(
  id: string | null | undefined,
  params: AccountPremiseListParams = {},
) {
  const merged: AccountPremiseListParams = {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
  return useQuery<AccountPremiseListResponse>({
    queryKey: premiseSubKey(id ?? "", merged),
    queryFn: async () => {
      if (!id) throw new Error("Account id is required");
      const { data, error, response } = await apiClient.GET("/accounts/{id}/premises", {
        params: { path: { id }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load account premises";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useAccountDeviceInstallations(
  id: string | null | undefined,
  params: AccountDeviceInstallationListParams = {},
) {
  const merged: AccountDeviceInstallationListParams = {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
  return useQuery<AccountDeviceInstallationListResponse>({
    queryKey: installationSubKey(id ?? "", merged),
    queryFn: async () => {
      if (!id) throw new Error("Account id is required");
      const { data, error, response } = await apiClient.GET("/accounts/{id}/device-installations", {
        params: { path: { id }, query: merged },
      });
      if (error) {
        const msg =
          (error as { error?: string }).error ?? "Failed to load account device installations";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * The devices whose meter names this account. Billing admits nothing to the
 * account's own devices, so this list is read apart from
 * `useAccountDeviceInstallations`. Keyed on the shared `meterKeys.billedByAccount`
 * rather than an `accountKeys.detail` sub-key: which devices the list holds,
 * and the meter number each row prints, are METER state — re-pointing a meter's
 * account, linking or unlinking its devices, or renumbering it all change them
 * — so a meter write, which invalidates `meterKeys.all`, is what must flush
 * them. An account PATCH changes nothing these rows carry.
 */
export function useAccountBilledInstallations(
  id: string | null | undefined,
  params: AccountBilledInstallationListParams = {},
) {
  // Left un-annotated: `meterKeys.billedByAccount` takes a
  // `Record<string, unknown>`, which the spread's inferred object type satisfies
  // through TS's implicit index signature while the named interface does not.
  const merged = {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
  return useQuery<AccountBilledInstallationListResponse>({
    queryKey: meterKeys.billedByAccount(id ?? "", merged),
    queryFn: async () => {
      if (!id) throw new Error("Account id is required");
      const { data, error, response } = await apiClient.GET("/accounts/{id}/billed-installations", {
        params: { path: { id }, query: merged },
      });
      if (error) {
        const msg =
          (error as { error?: string }).error ?? "Failed to load account billed installations";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * The meters billed to this account, entered from the account side. Keyed on
 * the shared `meterKeys.byAccount` rather than an `accountKeys.detail` sub-key:
 * the rows are METER state (number, premise, served-assembly count), so a meter
 * write — which invalidates `meterKeys.all` — is what must flush them, not an
 * account PATCH.
 */
export function useAccountMeters(
  id: string | null | undefined,
  params: AccountMeterListParams = {},
) {
  // Left un-annotated (and spread-free): `meterKeys.byAccount` takes a
  // `Record<string, unknown>`, which an inferred object-literal type satisfies
  // through TS's implicit index signature while a named interface does not.
  const merged = {
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
  return useQuery<AccountMeterListResponse>({
    queryKey: meterKeys.byAccount(id ?? "", merged),
    queryFn: async () => {
      if (!id) throw new Error("Account id is required");
      const { data, error, response } = await apiClient.GET("/accounts/{id}/meters", {
        params: { path: { id }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load account meters";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}
